/**
 * Word-based FAQ search. A query matches an item when EVERY meaningful word of
 * the query appears somewhere in the question or answer, so "how to book pt"
 * finds "How do I book a PT session…" even though the exact phrase never occurs.
 * Filler words are ignored and simple word endings are stripped ("booking" ~ "book").
 */

// Filler words that don't help find an answer (English + Arabic).
const STOPWORDS = new Set([
  "a", "an", "the", "to", "do", "does", "did", "i", "we", "you", "my", "me", "is", "are", "am", "be", "it",
  "how", "what", "why", "when", "where", "which", "who", "can", "could", "should", "would", "will", "of", "in",
  "on", "for", "and", "or", "with", "from", "at", "by", "this", "that", "there", "get", "if", "so",
  "كيف", "ما", "ماذا", "لماذا", "متى", "اين", "هل", "من", "في", "على", "الى", "عن", "او", "و", "ان", "هذا", "هذه",
]);

/** Lowercase, strip Latin accents and Arabic diacritics, unify Arabic letter variants. */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ًͯ-ٰٟـ]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[^\p{L}\p{N}\s]/gu, " ");
}

/** Strips a common English ending so "booking"/"booked"/"books" all search for "book". */
function stem(w: string): string {
  if (w.length > 5 && w.endsWith("ing")) return w.slice(0, -3);
  if (w.length > 4 && w.endsWith("ed")) return w.slice(0, -2);
  if (w.length > 4 && w.endsWith("es")) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith("s") && !w.endsWith("ss")) return w.slice(0, -1);
  return w;
}

/** Meaningful search terms for a query (stopwords removed, stemmed, de-duplicated). */
export function searchTerms(query: string): string[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  const meaningful = words.filter((w) => !STOPWORDS.has(w));
  // A query made only of filler words ("how to") still searches for what was typed.
  return [...new Set((meaningful.length ? meaningful : words).map(stem))];
}

function words(text: string): string[] {
  return normalize(text).split(/\s+/).filter(Boolean);
}

/** Best match of one term against a word list: 2 = exact word, 1 = word starts with term, 0 = none. */
function termHit(ws: string[], term: string): number {
  let best = 0;
  for (const w of ws) {
    if (w === term) return 2;
    if (w.startsWith(term)) best = 1;
  }
  return best;
}

/**
 * 0 = no match; higher = more relevant. Terms only match at the START of a word
 * ("pt" never matches inside "captured"). Question hits weigh far more than
 * answer hits, exact words more than prefixes, and a question containing every
 * term gets a bonus.
 */
export function scoreItem(item: { q: string; a: string }, terms: string[]): number {
  if (terms.length === 0) return 1;
  const qw = words(item.q);
  const aw = words(item.a);
  let score = 0;
  let inQuestion = 0;
  for (const t of terms) {
    const q = termHit(qw, t);
    const a = termHit(aw, t);
    if (!q && !a) return 0;
    if (q) {
      inQuestion++;
      score += 8 * q;
    } else {
      score += a;
    }
  }
  if (inQuestion === terms.length) score += 20;
  return score;
}

/** Splits text into [text, isMatch] chunks for highlighting the search terms. */
export function highlightParts(text: string, terms: string[]): Array<[string, boolean]> {
  if (terms.length === 0) return [[text, false]];
  const norm = normalize(text);
  // normalize() preserves string length for the characters we handle, so offsets line up.
  if (norm.length !== text.length) return [[text, false]];
  const marks = new Array<boolean>(text.length).fill(false);
  for (const t of terms) {
    let from = 0;
    for (;;) {
      const i = norm.indexOf(t, from);
      if (i === -1) break;
      from = i + 1;
      if (i > 0 && !/\s/.test(norm[i - 1])) continue; // only match at the start of a word
      const end = (() => {
        let e = i + t.length;
        while (e < norm.length && !/\s/.test(norm[e])) e++; // highlight the whole word ("book" -> "booking")
        return e;
      })();
      for (let k = i; k < end; k++) marks[k] = true;
    }
  }
  const out: Array<[string, boolean]> = [];
  let start = 0;
  for (let i = 1; i <= text.length; i++) {
    if (i === text.length || marks[i] !== marks[start]) {
      out.push([text.slice(start, i), marks[start]]);
      start = i;
    }
  }
  return out;
}
